import { useEffect, useId, useState, type FormEvent } from 'react';
import {
  CLES_REGLAGES_ENTIERS,
  CLE_TELEPHONES_EXCLUS,
  LIBELLE_NUMERO_EXCLU_MAX,
  REGLAGES_ENTIERS,
  telephoneLisible,
  validerParametre,
  type DetailValidation,
  type NumeroExclu,
  type ParametreLu,
  type ParametresLus,
  type ReglageEntier,
} from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { NavAdministration } from '../composants/NavAdministration';

type Enregistrer = (cle: string, valeur: unknown) => Promise<ParametreLu>;

/** « Modifié le 15/09/2026 14:30 par Fadhel » ; sans auteur, valeur posée par la migration ou le semis. */
function modification(parametre: ParametreLu): string {
  const quand = new Date(parametre.modifieLe).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  return parametre.modifiePar
    ? `Modifié le ${quand} par ${parametre.modifiePar}`
    : `Valeur initiale, posée le ${quand}`;
}

/** Détails d'un refus 400 REQUETE_INVALIDE de l'API. */
function detailsDe(erreur: unknown): DetailValidation[] {
  const details = (erreur as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return [];
  return details.filter(
    (d): d is DetailValidation => typeof d?.champ === 'string' && typeof d?.message === 'string',
  );
}

function lireNumeros(valeur: unknown): { liste: NumeroExclu[]; illisible: boolean } {
  if (!Array.isArray(valeur)) return { liste: [], illisible: valeur !== undefined };
  const liste = valeur.filter(
    (e): e is NumeroExclu => typeof e?.numero === 'string' && typeof e?.libelle === 'string',
  );
  return { liste, illisible: liste.length !== valeur.length };
}

interface ErreursNumero {
  numero?: string;
  libelle?: string;
  general?: string;
}

/** Range les détails de validation : ceux de la ligne saisie sous ses champs, les autres en tête. */
function erreursDe(details: readonly DetailValidation[], ligneSaisie: number | null): ErreursNumero {
  const erreurs: ErreursNumero = {};
  const generales: string[] = [];
  for (const detail of details) {
    if (ligneSaisie !== null && detail.champ === `valeur.${ligneSaisie}.numero`) erreurs.numero ??= detail.message;
    else if (ligneSaisie !== null && detail.champ === `valeur.${ligneSaisie}.libelle`) erreurs.libelle ??= detail.message;
    else generales.push(detail.message);
  }
  if (generales.length > 0) erreurs.general = generales.join(' · ');
  return erreurs;
}

/** « Numéros à ne jamais attribuer à un candidat » (paramètre `cv.telephones_exclus`). */
function NumerosExclus({ parametre, enregistrer }: { parametre: ParametreLu | null; enregistrer: Enregistrer }) {
  const id = useId();
  const [numero, setNumero] = useState('');
  const [libelle, setLibelle] = useState('');
  const [erreurs, setErreurs] = useState<ErreursNumero>({});
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { liste, illisible } = lireNumeros(parametre?.valeur);

  /** Valide la nouvelle liste avec le schéma partagé, puis l'enregistre telle que normalisée. */
  async function envoyer(nouvelle: unknown[], ligneSaisie: number | null, succes: string): Promise<boolean> {
    setMessage(null);
    const controle = validerParametre(CLE_TELEPHONES_EXCLUS, nouvelle);
    if (controle.statut !== 'valide') {
      setErreurs(controle.statut === 'invalide' ? erreursDe(controle.details, ligneSaisie) : { general: 'Paramètre inconnu' });
      return false;
    }
    setEnvoi(true);
    setErreurs({});
    try {
      await enregistrer(CLE_TELEPHONES_EXCLUS, controle.valeur);
      setMessage(succes);
      return true;
    } catch (e) {
      const details = detailsDe(e);
      setErreurs(
        details.length > 0
          ? erreursDe(details, ligneSaisie)
          : { general: e instanceof Error ? e.message : 'Enregistrement impossible' },
      );
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  async function ajouter(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (await envoyer([...liste, { numero, libelle }], liste.length, 'Numéro ajouté.')) {
      setNumero('');
      setLibelle('');
    }
  }

  async function supprimer(index: number): Promise<void> {
    const entree = liste[index];
    if (!entree) return;
    const lisible = telephoneLisible(entree.numero);
    if (
      !window.confirm(
        `Retirer le ${lisible} (${entree.libelle}) de la liste ? Il pourra de nouveau être pris pour le téléphone d’un candidat.`,
      )
    ) {
      return;
    }
    await envoyer(
      liste.filter((_, i) => i !== index),
      null,
      'Numéro retiré.',
    );
  }

  return (
    <section className="bloc">
      <h3>Numéros à ne jamais attribuer à un candidat</h3>
      <p className="mention">
        Siège, points de vente, plateformes : un numéro de cette liste lu dans un CV n’est jamais pris pour celui du
        candidat, ni pour rattacher le CV à une fiche.
      </p>
      {!parametre ? (
        <p className="signal">Paramètre absent de la base : appliquer la migration 013 avant de le remplir.</p>
      ) : (
        <>
          <p className="mention">{modification(parametre)}</p>
          {illisible && (
            <p className="signal">
              Valeur enregistrée en partie illisible : les entrées invalides disparaîtront au prochain enregistrement.
            </p>
          )}
          {liste.length === 0 ? (
            <Vide enfants="Aucun numéro pour l’instant." />
          ) : (
            <ul className="numeros-exclus">
              {liste.map((entree, index) => (
                <li key={entree.numero}>
                  <span className="numero-exclu">{telephoneLisible(entree.numero)}</span>
                  <span className="libelle">{entree.libelle}</span>
                  <button
                    type="button"
                    className="bouton-second bouton-danger"
                    disabled={envoi}
                    aria-label={`Supprimer ${telephoneLisible(entree.numero)} (${entree.libelle})`}
                    onClick={() => void supprimer(index)}
                  >
                    Supprimer
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form className="ajout-numero" noValidate onSubmit={(e) => void ajouter(e)}>
            <div className="champ">
              <label htmlFor={`${id}-numero`}>Numéro</label>
              <input
                id={`${id}-numero`}
                type="tel"
                inputMode="tel"
                autoComplete="off"
                value={numero}
                maxLength={40}
                placeholder="71 234 567"
                aria-invalid={Boolean(erreurs.numero)}
                onChange={(e) => setNumero(e.target.value)}
              />
              {erreurs.numero && <span className="alerte">{erreurs.numero}</span>}
            </div>
            <div className="champ">
              <label htmlFor={`${id}-libelle`}>Libellé</label>
              <input
                id={`${id}-libelle`}
                type="text"
                value={libelle}
                maxLength={LIBELLE_NUMERO_EXCLU_MAX}
                placeholder="Siège Tiba Invest"
                aria-invalid={Boolean(erreurs.libelle)}
                onChange={(e) => setLibelle(e.target.value)}
              />
              {erreurs.libelle && <span className="alerte">{erreurs.libelle}</span>}
            </div>
            <button type="submit" className="bouton" disabled={envoi}>
              Ajouter
            </button>
          </form>
          {erreurs.general && <Erreur message={erreurs.general} />}
          {message && (
            <p className="notice-cv" role="status">
              {message}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function bornes(reglage: ReglageEntier): string {
  // « sur 5 » se lit mal après un nombre : « De 1 à 5 » suffit.
  const unite = reglage.unite.startsWith('sur ') ? '' : ` ${reglage.unite}`;
  return `De ${reglage.min} à ${reglage.max}${unite} · par défaut ${reglage.defaut}${unite}`;
}

/** Un réglage entier : libellé, unité, bornes, dernière modification. */
function ReglageLigne({
  reglage,
  parametre,
  enregistrer,
}: {
  reglage: ReglageEntier;
  parametre: ParametreLu | null;
  enregistrer: Enregistrer;
}) {
  const id = useId();
  const actuelle =
    typeof parametre?.valeur === 'number' && Number.isInteger(parametre.valeur) ? parametre.valeur : null;
  const [saisie, setSaisie] = useState(actuelle === null ? '' : String(actuelle));
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [enregistre, setEnregistre] = useState(false);

  async function valider(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    const brut = saisie.trim();
    const controle = validerParametre(reglage.cle, brut === '' ? undefined : Number(brut));
    if (controle.statut !== 'valide') {
      setErreur(controle.statut === 'invalide' ? (controle.details[0]?.message ?? 'Valeur invalide') : 'Paramètre inconnu');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    setEnregistre(false);
    try {
      const lu = await enregistrer(reglage.cle, controle.valeur);
      setSaisie(String(lu.valeur));
      setEnregistre(true);
    } catch (e) {
      setErreur(detailsDe(e)[0]?.message ?? (e instanceof Error ? e.message : 'Enregistrement impossible'));
    } finally {
      setEnvoi(false);
    }
  }

  const inchangee = actuelle !== null && saisie.trim() === String(actuelle);
  const nonAppliquee = reglage.cle === 'retention.employes.mois';

  return (
    <li className="reglage">
      <form noValidate onSubmit={(e) => void valider(e)}>
        <label htmlFor={id} className="libelle-reglage">
          {reglage.libelle}
        </label>
        <div className="saisie-reglage">
          <input
            id={id}
            type="number"
            inputMode="numeric"
            min={reglage.min}
            max={reglage.max}
            step={1}
            value={saisie}
            disabled={!parametre}
            aria-describedby={`${id}-bornes`}
            aria-invalid={Boolean(erreur)}
            onChange={(e) => {
              setSaisie(e.target.value);
              setErreur(null);
              setEnregistre(false);
            }}
          />
          <span className="unite">{reglage.unite}</span>
          <button type="submit" className="bouton" disabled={!parametre || envoi || inchangee}>
            Enregistrer
          </button>
        </div>
        <p id={`${id}-bornes`} className="mention">
          {bornes(reglage)}
        </p>
        {reglage.aide && (
          <p className={nonAppliquee ? 'signal-alerte aide-reglage' : 'mention'}>{reglage.aide}</p>
        )}
        {parametre ? (
          <p className="mention">{modification(parametre)}</p>
        ) : (
          <p className="signal">
            Absent de la base : la valeur par défaut ({reglage.defaut} {reglage.unite}) s’applique.
          </p>
        )}
        {parametre && actuelle === null && (
          <p className="signal">
            Valeur enregistrée invalide : la valeur par défaut ({reglage.defaut} {reglage.unite}) s’applique.
          </p>
        )}
        {erreur && <Erreur message={erreur} />}
        {enregistre && (
          <p className="notice-cv" role="status">
            Enregistré.
          </p>
        )}
      </form>
    </li>
  );
}

/** Écran « Paramètres » (docs/UI.md § Écran 6), super admin seulement. */
export function Parametres() {
  const [parametres, setParametres] = useState<ParametresLus | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<ParametresLus>('/parametres')
      .then(setParametres)
      .catch((e: Error) => setErreur(e.message));
  }, []);

  async function enregistrer(cle: string, valeur: unknown): Promise<ParametreLu> {
    const lu = await appel<ParametreLu>(`/parametres/${encodeURIComponent(cle)}`, {
      method: 'PUT',
      body: JSON.stringify({ valeur }),
    });
    setParametres((actuels) => (actuels ? { ...actuels, [cle]: lu } : actuels));
    return lu;
  }

  if (erreur) return <Erreur message={erreur} />;
  if (!parametres) return <Chargement quoi="des paramètres" />;

  return (
    <section className="parametres">
      <NavAdministration />
      <div className="entete-annonce">
        <h2>Paramètres</h2>
      </div>
      <p className="mention">Réservé au super admin. Chaque modification est journalisée.</p>
      <NumerosExclus parametre={parametres[CLE_TELEPHONES_EXCLUS] ?? null} enregistrer={enregistrer} />
      <section className="bloc">
        <h3>Durées et seuils</h3>
        <ul className="reglages">
          {CLES_REGLAGES_ENTIERS.map((cle) => (
            <ReglageLigne
              key={cle}
              reglage={REGLAGES_ENTIERS[cle]}
              parametre={parametres[cle] ?? null}
              enregistrer={enregistrer}
            />
          ))}
        </ul>
      </section>
    </section>
  );
}
